import { uniteOfMeasure } from '@ab-core-functions/measure-units';
import type { CounterType } from '@ab-core/counter';
import { CounterWithTooltip } from '@ab-core/counter';
import { getClassNames } from '@ab-core/functions/styles/classNameFormatter';
import type { CableSnap, PositionPriceObject } from '@ab-core/graphql/dist/index';
import getActiveCart from '@ab-core/hooks/cartNew/activeCart/useActiveCart';
import { Icon } from '@ab-core/icon';
import { productStore } from '@ab-core/store';
import { getTestIdProp } from '@ab-core/testing';
import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import type { ScalableStyle } from '../../../scalableStyles';
import { IconWrapper, QuantityPickerWrapper } from './styled';

export type QuantityPickerStyleType = {
    isVisible?: boolean;
    showCalculator?: boolean;
    showCableSnap?: boolean;
    pickerDisabled?: boolean;
};

type QuantityPickerType = ScalableStyle<QuantityPickerStyleType> & {
    onQuantityChange?: (q: number) => void;
    quantityUnit: string;
    positionPrice?: PositionPriceObject;
    salesUnit: number;
    quantity: number;
    maxQuantity?: number;
    sku: string;
    isPackageItem?: boolean;
    cableSnaps?: Array<CableSnap>;
};

export const QuantityPicker: FC<QuantityPickerType> = (props) => {
    const {
        onQuantityChange,
        quantityUnit,
        salesUnit,
        positionPrice,
        quantity,
        maxQuantity,
        sku,
        scalableStyle,
        cableSnaps,
        isPackageItem
    } = props;
    const counterQuantityUnit = quantityUnit ? uniteOfMeasure.get(quantityUnit) : undefined;

    const { activeCart } = getActiveCart();
    const [availableCableSnapData, setAvailableCableSnapData] = useState<Array<CableSnap>>([]);
    const [hasAvailableCableSnapData, setHasAvailableCableSnapData] = useState(false);

    const counterStateUpdate: CounterType['stateUpdate'] = (state) => {
        if (!onQuantityChange) {
            return;
        }

        onQuantityChange(Number(state.quantity || '1'));
    };
    const emitCalculatorStates = () => {
        productStore.setters.setShowCalculator(true);
        productStore.setters.setQuantity(quantity);

        if (positionPrice) {
            productStore.setters.setPrice(positionPrice);
        }

        productStore.setters.setProduct({
            quantityUnit,
            sku
        });
    };

    useEffect(() => {
        if (!activeCart) {
            return;
        }

        if (!activeCart.lineItemsV2) {
            return;
        }

        const availableData: Array<CableSnap> = [];

        cableSnaps?.forEach((cableSnap) => {
            const isCableSnapInCart = activeCart.lineItemsV2?.some(
                (lineItem) => lineItem.chargeNr === cableSnap?.chargeNr?.toString()
            );

            if (!isCableSnapInCart) {
                availableData.push(cableSnap);
            }
        });

        setAvailableCableSnapData(availableData);
        setHasAvailableCableSnapData(availableData.length > 0);
    }, [activeCart?.lineItemsV2]);

    const handleOpenCableSnap = () => {
        productStore.setters.setCableSnapSku(sku);
        productStore.setters.setCableSnapData(availableCableSnapData || []);
        productStore.setters.setShowCableSnap(true);
    };

    if (scalableStyle?.isVisible === false) {
        return <></>;
    }

    const showCableSnapIcon = scalableStyle?.showCableSnap !== false && hasAvailableCableSnapData;

    const quantityPickerClasses = getClassNames({
        prefix: 'show-picker',
        modifier: {
            calculator: scalableStyle?.showCalculator,
            cablesnap: showCableSnapIcon
        }
    });

    return (
        <QuantityPickerWrapper className={quantityPickerClasses}>
            <CounterWithTooltip
                quantityUnit={counterQuantityUnit}
                quantity={quantity || salesUnit}
                step={salesUnit}
                stateUpdate={counterStateUpdate}
                disabled={isPackageItem || scalableStyle?.pickerDisabled}
                max={maxQuantity}
            />

            {showCableSnapIcon && (
                <IconWrapper onClick={handleOpenCableSnap}>
                    <Icon name="Discount" color="primary" />
                </IconWrapper>
            )}

            {scalableStyle?.showCalculator && (
                <IconWrapper onClick={emitCalculatorStates} {...getTestIdProp('open-price-calculation-modal-button')}>
                    <Icon name="Calculator" />
                </IconWrapper>
            )}
        </QuantityPickerWrapper>
    );
};
